import { useEffect, useEffectEvent, useRef, useState } from 'react';

import { useAtom } from 'jotai';

import { PROCEDURE_STEPS } from '@/components/popups/AddProcedurePopUp/constants';
import type { PopupDocument } from '@/components/popups/AddProcedurePopUp/types';

import { ProcedureDraft, procedureDraftAtom } from '../state';

export interface ProcedureSearch {
  displayed: string;
  query: string;
}

const DEFAULT_SEARCH: ProcedureSearch = {
  displayed: '',
  query: '',
};

type ProcedureStep = (typeof PROCEDURE_STEPS)[keyof typeof PROCEDURE_STEPS];

interface UseProcedureDraftOptions {
  isOpen: boolean;
  planId: string | undefined;
  sectionId: string;
  isInSection: boolean;
}

export const useProcedureState = ({
  isOpen,
  planId,
  sectionId,
  isInSection,
}: UseProcedureDraftOptions) => {
  const [storedDraft, setStoredDraft] = useAtom(procedureDraftAtom);
  const hasInitializedRef = useRef(false);

  const [activeStep, setActiveStep] = useState<ProcedureStep>(
    PROCEDURE_STEPS.CATEGORIES,
  );
  const [search, setSearch] = useState(DEFAULT_SEARCH);
  const [selectedDocuments, setSelectedDocuments] = useState<PopupDocument[]>(
    [],
  );
  const [categorySelected, setCategorySelected] = useState<unknown>(null);
  const [categoryName, setCategoryName] = useState('');
  const [preservedSubcategoryIds, setPreservedSubcategoryIds] = useState<
    string[]
  >([]);
  const [previewDocument, setPreviewDocument] = useState<
    Record<string, unknown>
  >({});

  const handleIsOpenChange = useEffectEvent((open: boolean) => {
    if (open && !hasInitializedRef.current) {
      hasInitializedRef.current = true;
      if (
        storedDraft &&
        storedDraft.planId === planId &&
        storedDraft.sectionId === sectionId
      ) {
        setActiveStep(storedDraft.activeStep as ProcedureStep);
        setSearch(
          (storedDraft.search as ProcedureSearch | undefined) ?? DEFAULT_SEARCH,
        );
        setSelectedDocuments(
          (storedDraft.selectedDocuments as PopupDocument[] | undefined) ?? [],
        );
        setCategorySelected(storedDraft.categorySelected ?? null);
        setCategoryName(storedDraft.categoryName ?? '');
        setPreservedSubcategoryIds(storedDraft.preservedSubcategoryIds ?? []);
        if (storedDraft.previewDocument) {
          setPreviewDocument(
            storedDraft.previewDocument as Record<string, unknown>,
          );
        }
      }
    }
    if (!open && hasInitializedRef.current) {
      setActiveStep(PROCEDURE_STEPS.CATEGORIES);
      setSearch(DEFAULT_SEARCH);
      setSelectedDocuments([]);
      setCategorySelected(null);
      setCategoryName('');
      setPreservedSubcategoryIds([]);
      setPreviewDocument({});
      hasInitializedRef.current = false;
    }
  });

  useEffect(() => {
    handleIsOpenChange(isOpen);
  }, [isOpen]);

  const saveDraft = useEffectEvent(() => {
    if (!hasInitializedRef.current || !isOpen) return;

    // Don't persist the PREVIEW step — it's transient. If the draft is
    // restored (e.g. after navigation away while the modal was open) we want
    // to land on the step the user came FROM, not re-enter preview on open.
    const savedStep =
      activeStep === PROCEDURE_STEPS.PREVIEW
        ? search.query
          ? PROCEDURE_STEPS.SEARCH
          : PROCEDURE_STEPS.SUBCATEGORIES
        : activeStep;

    const draft: ProcedureDraft = {
      activeStep: savedStep,
      search,
      selectedDocuments:
        selectedDocuments as unknown as ProcedureDraft['selectedDocuments'],
      categorySelected: categorySelected,
      categoryName,
      preservedSubcategoryIds,
      previewDocument,
      planId: planId ?? null,
      sectionId,
      isInSection,
      savedAt: Date.now(),
    };

    setStoredDraft(draft);
  });

  useEffect(() => {
    if (!isOpen) return;
    const timeoutId = setTimeout(saveDraft, 300);
    return () => clearTimeout(timeoutId);
  }, [
    isOpen,
    activeStep,
    search,
    selectedDocuments,
    categorySelected,
    categoryName,
    preservedSubcategoryIds,
    previewDocument,
  ]);

  const resetDraft = () => {
    setActiveStep(PROCEDURE_STEPS.CATEGORIES);
    setSearch(DEFAULT_SEARCH);
    setSelectedDocuments([]);
    setCategorySelected(null);
    setCategoryName('');
    setPreservedSubcategoryIds([]);
    setPreviewDocument({});
    setStoredDraft(null);
    hasInitializedRef.current = false;
  };

  return {
    activeStep,
    setActiveStep,
    search,
    setSearch,
    selectedDocuments,
    setSelectedDocuments,
    categorySelected,
    setCategorySelected,
    categoryName,
    setCategoryName,
    preservedSubcategoryIds,
    setPreservedSubcategoryIds,
    previewDocument,
    setPreviewDocument,
    resetDraft,
  };
};
